// Switchboard dashboard SPA. Read-only: the only writes are the dashboard's own sign-in/passkey/logout calls.
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { getJson, HttpError, postJson, type AuthState, type NeedsItem, type Overview, type OverviewProject, type ProjectDetail } from "./api.ts";
import { absTime, h, relTime, svg } from "./dom.ts";
import type { ProviderCapacity, RefEventSnap, RunSnap } from "../src/shared/types.ts";

const app = document.getElementById("app")!;
const freshnessEl = document.getElementById("freshness")!;
const refreshBtn = document.getElementById("refresh") as HTMLButtonElement;
const settingsLink = document.getElementById("settings-link")!;
const offlineBanner = document.getElementById("offline")!;

const REFRESH_MS = 60_000;
let lastGeneratedAt: string | null = null;
let staleAfterSeconds = 900;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

// ---------- routing ----------

function navigate(path: string): void {
  if (location.pathname !== path) history.pushState(null, "", path);
  void route();
}

document.addEventListener("click", (e) => {
  const a = (e.target as Element).closest?.("a[data-link]") as HTMLAnchorElement | null;
  if (!a || e.metaKey || e.ctrlKey || e.shiftKey || a.origin !== location.origin) return;
  e.preventDefault();
  navigate(a.pathname);
});
window.addEventListener("popstate", () => void route());
refreshBtn.addEventListener("click", () => void route());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && isDataRoute()) void route();
});

const isDataRoute = () => location.pathname === "/" || location.pathname.startsWith("/p/");

function setChrome(signedIn: boolean): void {
  refreshBtn.hidden = !signedIn;
  settingsLink.hidden = !signedIn;
  if (!signedIn) freshnessEl.hidden = true;
}

function render(...nodes: (Node | null | false)[]): void {
  app.replaceChildren(...nodes.filter((n): n is Node => !!n));
}

async function route(): Promise<void> {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = null;
  const path = location.pathname;
  try {
    if (path === "/settings") return await settingsView();
    const m = /^\/p\/([a-z0-9][a-z0-9_-]{0,63})$/.exec(path);
    if (m) await projectView(m[1]!);
    else await overviewView();
    refreshTimer = setInterval(() => {
      if (document.visibilityState === "visible") void route();
    }, REFRESH_MS);
  } catch (e) {
    if (e instanceof HttpError && e.status === 401) return authView();
    render(h("div", { class: "banner bad" }, `Could not load data (${e instanceof HttpError ? e.code : "network error"}).`), h("button", { class: "btn", type: "button", onclick: () => void route() }, "Try again"));
  }
}

// ---------- freshness ----------

function updateFreshness(): void {
  if (!lastGeneratedAt) return;
  const age = (Date.now() - Date.parse(lastGeneratedAt)) / 1000;
  const stale = age > staleAfterSeconds;
  freshnessEl.hidden = false;
  freshnessEl.textContent = `${stale ? "Stale · " : ""}updated ${relTime(lastGeneratedAt)}`;
  freshnessEl.title = `Snapshot generated ${absTime(lastGeneratedAt)}`;
  freshnessEl.classList.toggle("stale", stale);
  document.getElementById("stale-banner")?.toggleAttribute("hidden", !stale);
}
setInterval(updateFreshness, 30_000);

function trackFreshness(f: { generated_at: string; stale_after_seconds: number }, offline: boolean): HTMLElement {
  lastGeneratedAt = f.generated_at;
  staleAfterSeconds = f.stale_after_seconds;
  offlineBanner.hidden = !offline;
  setChrome(true);
  const banner = h("div", { id: "stale-banner", class: "banner warn", hidden: true }, `Data is stale: the last snapshot is from ${relTime(f.generated_at)}. The exporter on Ampere may not be running.`);
  queueMicrotask(updateFreshness);
  return banner;
}

// ---------- shared bits ----------

function statusBadge(m: OverviewProject["metrics"]): HTMLElement {
  if (m.paused) return h("span", { class: "badge paused" }, "Paused");
  if (m.runs_in_flight > 0) return h("span", { class: "badge active" }, "Active");
  return h("span", { class: "badge" }, "Idle");
}

function refChip(e: RefEventSnap): HTMLElement {
  const label = e.pr_number ? `#${e.pr_number}` : (e.ref?.replace(/^refs\/heads\//, "") ?? "ref");
  return h("span", { class: `ref ${e.code}`, title: `${e.code}${e.reason ? ` (${e.reason})` : ""} · ${absTime(e.ts)}` }, `${label} ${e.code.replace(/_/g, " ")}`);
}

function progressBar(p: number | null, label: string): (HTMLElement | null)[] {
  if (p === null) return [null];
  const pct = Math.round(p * 100);
  return [h("div", { class: "progress", role: "progressbar", "aria-valuenow": pct, "aria-valuemin": 0, "aria-valuemax": 100, "aria-label": label }, h("span", { style: `width:${pct}%` })), h("div", { class: "small muted" }, label)];
}

const KIND_LABEL: Record<NeedsItem["kind"], string> = { approval: "Approval", decision: "Decision", attention: "Attention", parked: "Parked work" };

function needsSection(items: NeedsItem[], showProject: boolean): HTMLElement {
  if (items.length === 0) return h("section", { class: "needs clear" }, h("h2", null, "✓ Nothing needs you"));
  return h(
    "section",
    { class: "needs", "aria-label": "Needs you" },
    h("h2", null, "Needs you ", h("span", { class: "count" }, items.length)),
    h(
      "ul",
      null,
      items.map((n) =>
        h(
          "li",
          null,
          h("div", { class: "row" }, h("strong", null, KIND_LABEL[n.kind]), h("span", { class: "small muted" }, [showProject && n.project_id ? h("a", { href: `/p/${n.project_id}`, "data-link": true }, n.project_id) : null, n.at ? ` · ${relTime(n.at)}` : ""])),
          h("div", { class: n.kind === "decision" ? "untrusted" : "" }, n.text),
          n.detail?.length ? h("ol", { class: "small" }, n.detail.map((d) => h("li", null, d.replace(/^\d+\.\s*/, "")))) : null,
        ),
      ),
    ),
    h("p", { class: "small muted" }, "Act on these in Switchboard; this dashboard is read-only."),
  );
}

function capacitySection(cap: Overview["capacity"]): HTMLElement | null {
  if (!cap || cap.providers.length === 0) return null;
  const sorted = [...cap.providers].sort((a, b) => Number(b.enabled) - Number(a.enabled) || a.provider.localeCompare(b.provider));
  const row = (p: ProviderCapacity) => {
    const score = p.capacity_score;
    const risk = p.throttle_risk ?? "unknown";
    const slots = p.concurrency_limit !== null ? `${p.concurrency_available ?? "?"}/${p.concurrency_limit} slots` : "";
    const breaker = p.breaker_state && p.breaker_state !== "CLOSED" ? ` · breaker ${p.breaker_state}` : "";
    return h(
      "div",
      { class: `cap-row${p.enabled === false ? " disabled" : ""}` },
      h("div", null, h("strong", null, p.provider), p.enabled === false ? h("div", { class: "small muted" }, "disabled") : null),
      h("div", { class: `meter ${risk}`, title: score === null ? "no recent evidence" : `capacity ${Math.round(score * 100)}%` }, h("span", { style: `width:${score === null ? 0 : Math.round(score * 100)}%` })),
      h("div", { class: "small muted" }, `${risk} risk${slots ? ` · ${slots}` : ""}${breaker}${p.reset_at ? ` · resets ${relTime(p.reset_at)}` : ""}`),
    );
  };
  return h("section", null, h("h2", null, "Provider capacity"), h("div", { class: "card capacity" }, sorted.map(row), h("p", { class: "small muted" }, `Controller estimates as of ${relTime(cap.as_of)}; not provider-reported quota.`)));
}

function errorsBanner(errors: { source: string; message: string }[]): HTMLElement | null {
  if (!errors.length) return null;
  return h("div", { class: "banner warn" }, `Partial data: the exporter could not read ${errors.map((e) => e.source).join(", ")}.`);
}

// ---------- overview ----------

function projectCard(p: OverviewProject): HTMLElement {
  const m = p.metrics;
  return h(
    "a",
    { class: "card project", href: `/p/${p.id}`, "data-link": true },
    h("div", { class: "project-head" }, h("h3", null, p.name), h("div", { class: "badges" }, m.needs_you ? h("span", { class: "badge needs" }, `${m.needs_you} need you`) : null, statusBadge(m))),
    h(
      "div",
      { class: "stats" },
      h("span", null, h("b", null, m.runs_in_flight), " in flight"),
      h("span", null, h("b", null, m.succeeded_24h), " ✓ / ", h("b", null, m.failed_24h), " ✗ 24h"),
      h("span", null, h("b", null, m.merges_7d), " merges 7d"),
    ),
    ...progressBar(m.progress, `Backlog ${m.work_done}/${m.work_total} done`),
    h("p", { class: "summary" }, p.summary),
    p.recent_refs.length ? h("div", { class: "refs" }, p.recent_refs.map(refChip)) : null,
  );
}

async function overviewView(): Promise<void> {
  const { data, offline } = await getJson<Overview>("/api/overview");
  if (data.empty) {
    setChrome(true);
    render(h("div", { class: "banner info" }, "No snapshot yet. The exporter on Ampere pushes one every few minutes."));
    return;
  }
  const stale = trackFreshness(data.freshness, offline);
  const projects = [...data.projects].sort((a, b) => b.metrics.needs_you - a.metrics.needs_you || b.metrics.runs_in_flight - a.metrics.runs_in_flight || Number(!!a.metrics.paused) - Number(!!b.metrics.paused) || a.name.localeCompare(b.name));
  document.title = data.needs_you.length ? `(${data.needs_you.length}) Switchboard` : "Switchboard";
  render(
    stale,
    errorsBanner(data.errors),
    h("p", { class: "headline" }, data.summary),
    h("div", { style: "margin-top:0.9rem" }, needsSection(data.needs_you, true)),
    h("h2", null, `Projects (${projects.length})`),
    h("div", { class: "grid" }, projects.map(projectCard)),
    capacitySection(data.capacity),
  );
}

// ---------- project detail ----------

function sparkline(series: ProjectDetail["series"]): HTMLElement | null {
  if (series.length < 2) return null;
  const W = 300, H = 60;
  const t0 = series[0]!.ts, t1 = series.at(-1)!.ts || t0 + 1;
  const path = (vals: number[]) => {
    const max = Math.max(1, ...vals);
    return vals.map((v, i) => `${i ? "L" : "M"}${(((series[i]!.ts - t0) / (t1 - t0 || 1)) * W).toFixed(1)},${(H - (v / max) * (H - 4) - 2).toFixed(1)}`).join(" ");
  };
  return h(
    "div",
    { class: "card" },
    h("div", { class: "row" }, h("strong", null, "Last 7 days"), h("div", { class: "legend" }, h("span", { class: "a" }, "runs in flight"), h("span", { class: "b" }, "backlog done"))),
    svg("svg", { class: "spark", viewBox: `0 0 ${W} ${H}`, preserveAspectRatio: "none", role: "img", "aria-label": "Activity over the last 7 days" }, svg("path", { class: "line", d: path(series.map((s) => s.runs_in_flight)) }), svg("path", { class: "line2", d: path(series.map((s) => s.work_done)) })) as unknown as HTMLElement,
  );
}

function timelineClass(kind: string): string {
  if (/succeeded|merged|ci_passed|resumed/.test(kind)) return "tl-ok";
  if (/failed|refused|conflicting|cancelled/.test(kind)) return "tl-bad";
  if (/approval|decision_open|paused/.test(kind)) return "tl-needs";
  return "tl-info";
}

function runItem(r: RunSnap): HTMLElement {
  const via = [r.provider, r.model].filter(Boolean).join(" · ");
  const facts = [via, r.changed_files !== null ? `${r.changed_files} files` : "", r.cost_usd !== null ? `$${r.cost_usd.toFixed(2)}` : "", r.failure_code ?? "", r.needs_attention ? `attention: ${r.needs_attention}` : ""].filter(Boolean);
  return h(
    "li",
    null,
    h("div", { class: "row" }, h("span", null, h("span", { class: `status ${r.status}` }, r.status), " ", h("span", { class: "mono muted" }, r.run_id)), h("span", { class: "small muted", title: absTime(r.created_at) }, relTime(r.created_at))),
    facts.length ? h("div", { class: "small muted" }, facts.join(" · ")) : null,
    r.summary ? h("p", { class: "untrusted", title: "Agent-written summary (untrusted text)" }, r.summary) : null,
  );
}

async function projectView(id: string): Promise<void> {
  let fetched;
  try {
    fetched = await getJson<ProjectDetail>(`/api/projects/${id}`);
  } catch (e) {
    if (e instanceof HttpError && e.status === 404) {
      setChrome(true);
      render(h("a", { class: "back", href: "/", "data-link": true }, "← All projects"), h("div", { class: "banner info" }, "This project is not in the latest snapshot."));
      return;
    }
    throw e;
  }
  const { data: d, offline } = fetched;
  const stale = trackFreshness(d.freshness, offline);
  const m = d.metrics;
  document.title = `${d.project.name ?? d.project.id} · Switchboard`;
  const tile = (v: string | number, k: string) => h("div", { class: "tile" }, h("div", { class: "v" }, v), h("div", { class: "k" }, k));
  const openWork = d.work.filter((w) => !["DONE", "CANCELLED"].includes(w.state));
  render(
    stale,
    h("a", { class: "back", href: "/", "data-link": true }, "← All projects"),
    h("div", { class: "project-head" }, h("h1", null, d.project.name ?? d.project.id), statusBadge(m as OverviewProject["metrics"])),
    h("p", { class: "summary" }, d.summary),
    h(
      "div",
      { class: "tiles" },
      tile(m.runs_in_flight, "runs in flight"),
      tile(`${m.succeeded_24h}/${m.failed_24h}`, "✓/✗ last 24h"),
      tile(m.merges_7d, "merges this week"),
      tile(m.progress === null ? "–" : `${Math.round(m.progress * 100)}%`, `backlog (${m.work_done}/${m.work_total})`),
      tile(m.runs_total, "runs on record"),
      tile(`$${m.cost_24h_usd.toFixed(2)}`, "reported cost 24h"),
    ),
    d.needs_you.length ? needsSection(d.needs_you, false) : null,
    sparkline(d.series),
    d.ref_events.length ? h("section", null, h("h2", null, "Pull requests & merges"), h("div", { class: "refs" }, d.ref_events.slice(0, 20).map(refChip))) : null,
    h("h2", null, "Timeline"),
    d.timeline.length
      ? h("div", { class: "card" }, h("ul", { class: "timeline" }, d.timeline.slice(0, 60).map((t) => h("li", { class: timelineClass(t.kind) }, h("div", { class: "row" }, h("span", null, t.text), h("span", { class: "small muted", title: absTime(new Date(t.ts).toISOString()) }, relTime(new Date(t.ts).toISOString())))))))
      : h("p", { class: "muted" }, "No events yet."),
    h("h2", null, `Recent runs (${d.runs.length})`),
    d.runs.length ? h("div", { class: "card" }, h("ul", { class: "list" }, d.runs.slice(0, 30).map(runItem))) : h("p", { class: "muted" }, "No runs."),
    openWork.length || d.work.length
      ? h(
          "section",
          null,
          h("h2", null, `Work queue (${openWork.length} open of ${d.work.length})`),
          h("div", { class: "card" }, h("ul", { class: "list" }, d.work.slice(0, 40).map((w) => h("li", null, h("div", { class: "row" }, h("span", null, h("span", { class: `status ${w.state}` }, w.state), " ", h("span", { class: "mono muted" }, w.work_item_id)), h("span", { class: "small muted" }, relTime(w.updated_at ?? w.created_at))), w.last_error_code ? h("div", { class: "small muted" }, w.last_error_code) : null)))),
        )
      : null,
  );
}

// ---------- auth ----------

function friendlyError(e: unknown): string {
  if (e instanceof HttpError) {
    if (e.code === "invalid_setup_code") return "That setup code is not valid (or has already been used).";
    if (e.code === "unknown_credential") return "This passkey is not registered for this dashboard.";
    return `Failed: ${e.code}`;
  }
  if (e instanceof Error && e.name === "NotAllowedError") return "Cancelled or timed out.";
  return e instanceof Error ? e.message : "Something went wrong.";
}

async function authView(): Promise<void> {
  setChrome(false);
  offlineBanner.hidden = true;
  const { data: state } = await getJson<AuthState>("/api/auth/state");
  if (state.authenticated) return void route();
  const msg = h("p", { class: "error", role: "alert" });
  const signIn = h("button", { class: "btn primary", type: "button" }, "Sign in with passkey");
  signIn.addEventListener("click", async () => {
    msg.textContent = "";
    signIn.disabled = true;
    try {
      const options = await postJson<Parameters<typeof startAuthentication>[0]["optionsJSON"]>("/api/auth/login/options");
      const response = await startAuthentication({ optionsJSON: options });
      await postJson("/api/auth/login/verify", { response });
      navigate(location.pathname === "/settings" ? "/" : location.pathname);
    } catch (e) {
      msg.textContent = friendlyError(e);
    } finally {
      signIn.disabled = false;
    }
  });
  const code = h("input", { type: "password", autocomplete: "one-time-code", placeholder: "One-time setup code", "aria-label": "Setup code", required: true }) as HTMLInputElement;
  const enrollForm = h(
    "form",
    null,
    h("label", { class: "small muted" }, state.enrolled ? "Recovery: enroll a new passkey with a freshly rotated setup code" : "First-time setup: enter the setup code from Ampere"),
    code,
    h("button", { class: "btn", type: "submit" }, "Create passkey"),
  );
  enrollForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    msg.textContent = "";
    try {
      const options = await postJson<Parameters<typeof startRegistration>[0]["optionsJSON"]>("/api/auth/register/options", { setup_code: code.value.trim() });
      const response = await startRegistration({ optionsJSON: options });
      await postJson("/api/auth/register/verify", { response, name: deviceName() });
      code.value = "";
      navigate("/");
    } catch (err) {
      msg.textContent = friendlyError(err);
    }
  });
  render(
    h(
      "div",
      { class: "auth card" },
      h("h1", null, "Switchboard dashboard"),
      h("p", { class: "muted" }, "Read-only view of all Switchboard projects. Sign in with your passkey."),
      state.enrolled ? signIn : null,
      state.setup_available || !state.enrolled ? enrollForm : null,
      msg,
    ),
  );
}

function deviceName(): string {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "Device";
  return `${os} passkey`;
}

// ---------- settings ----------

async function settingsView(): Promise<void> {
  const { data } = await getJson<{ passkeys: { id: string; name: string | null; created_at: number; last_used_at: number | null; current: boolean }[] }>("/api/auth/passkeys");
  setChrome(true);
  const msg = h("p", { class: "error", role: "alert" });
  const add = h("button", { class: "btn primary", type: "button" }, "Add another passkey");
  add.addEventListener("click", async () => {
    msg.textContent = "";
    try {
      const options = await postJson<Parameters<typeof startRegistration>[0]["optionsJSON"]>("/api/auth/register/options", {});
      const response = await startRegistration({ optionsJSON: options });
      await postJson("/api/auth/register/verify", { response, name: deviceName() });
      void settingsView();
    } catch (e) {
      msg.textContent = friendlyError(e);
    }
  });
  const logout = h("button", { class: "btn", type: "button" }, "Sign out");
  logout.addEventListener("click", async () => {
    await postJson("/api/auth/logout").catch(() => undefined);
    navigator.serviceWorker?.controller?.postMessage("clear-data");
    lastGeneratedAt = null;
    navigate("/");
  });
  const fmt = (t: number | null) => (t ? new Date(t).toLocaleString() : "never");
  render(
    h("a", { class: "back", href: "/", "data-link": true }, "← All projects"),
    h("h1", null, "Settings"),
    h("h2", null, "Passkeys"),
    h(
      "div",
      { class: "card" },
      h(
        "ul",
        { class: "list" },
        data.passkeys.map((p) => {
          const del = h("button", { class: "btn danger", type: "button", disabled: data.passkeys.length <= 1 }, "Remove");
          del.addEventListener("click", async () => {
            if (!confirm(`Remove passkey "${p.name ?? p.id.slice(0, 8)}" from this dashboard?`)) return;
            try {
              await postJson(`/api/auth/passkeys/${encodeURIComponent(p.id)}`, undefined, "DELETE");
              void settingsView();
            } catch (e) {
              msg.textContent = friendlyError(e);
            }
          });
          return h("li", null, h("div", { class: "row" }, h("div", null, h("strong", null, p.name ?? "Passkey"), p.current ? h("span", { class: "badge active", style: "margin-left:0.4rem" }, "this session") : null, h("div", { class: "small muted" }, `Added ${fmt(p.created_at)} · last used ${fmt(p.last_used_at)}`)), del));
        }),
      ),
    ),
    h("div", { style: "display:flex;gap:0.6rem;flex-wrap:wrap;margin-top:0.9rem" }, add, logout),
    msg,
    h("p", { class: "small muted" }, "Removing a passkey here only affects sign-in to this dashboard. Nothing in Switchboard can be changed from here."),
  );
}

// ---------- boot ----------

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}
void route();
